import React, { useState, useEffect } from 'react';
import PaymentForm from './components/PaymentForm';
import {
  Currency,
  Platform,
} from '../../../apps/commerce/checkout/CheckoutTypes';
import makePayment from '../../../apps/commerce/checkout/payment_providers/boosts_payments';
import trackPurchase from '../../../apps/commerce/tracking/ecommerce/trackPurchase';
import storageAvailable from '../../../helpers/storage_available';

export type BoostProductDetail = {
  id: number;
  number_of_ads: number;
  category: 'Extra Boost';
  department: 'Upgrade Extras';
  type: 'upgrade_extra';
  visibility: 'visible';
  boosts: 1;
  price: string;
  service_level: 1;
  duration: {
    quantity: 1;
    unit: 'year';
  };
  name: '1 Boost';
  selling_points: [];
};

export default function GetBoosts({
  boostProductDetails,
  advertId,
  csEmail,
  currency = 'gbp' as Currency,
  currencySymbol,
  domainString,
  offeredAdsCount = 1,
  phoneNumber,
  platform = 'mobile' as Platform,
  subDirectory = 'flatshare',
  userId,
  localisations
}) {
  const allowedArray = [...Array(offeredAdsCount * 4).keys()].map((i) => i + 1);
  const [isPaymentView, updateIsPaymentView] = useState(false);
  const [numberOfBoostsSelected, updateNumberOfBoostsSelected] = useState<
    number
  >(0);
  const [amountToPay, updateAmountToPay] = useState<number>(0);
  function handleSubmit(event) {
    event.preventDefault();
    updateIsPaymentView(true);
  }

  function closeIsPaymentView() {
    updateIsPaymentView(false);
  }

  useEffect(() => {
    if (boostProductDetails.price) {
      const newNumberOfBoostsSelected = 1;
      updateNumberOfBoostsSelected(newNumberOfBoostsSelected);
      const basePrice = parseInt(
        (parseFloat(boostProductDetails.price) * 100).toFixed(0),
        10,
      );
      updateAmountToPay(basePrice * newNumberOfBoostsSelected);
    }
  }, [boostProductDetails]);

  function handleNumberOfBoostsChange(event) {
    const newNumberOfBoostsSelected = parseInt(event.target.value, 10);
    updateNumberOfBoostsSelected(newNumberOfBoostsSelected);
    const basePrice = parseInt(
      (parseFloat(boostProductDetails.price) * 100).toFixed(0),
      10,
    );
    updateAmountToPay(basePrice * newNumberOfBoostsSelected);
  }

  function paymentSuccessCallback({ id }) {
    trackPurchase({
      currencyCode: currency,
      transactionId: id,
      totalAmount: amountToPay,
      products: [
        {
          id: boostProductDetails.id.toString(),
          category: boostProductDetails.category,
          name: boostProductDetails.name,
          price: parseInt(
            (parseFloat(boostProductDetails.price) * 100).toFixed(0),
            10,
          ),
          quantity: numberOfBoostsSelected,
        },
      ],
      asynchronous: true,
    });
    if (storageAvailable('localStorage')) {
      const orderDetails = {
        date: Date.now(),
        quantity: numberOfBoostsSelected,
      };
      return localStorage.setItem(
        'boosts-purchased',
        JSON.stringify(orderDetails),
      );
    }
    return true;
  }

  const paymentSuccess = (provider, token) => {
    const products = [
      {
        id: boostProductDetails.id.toString(),
        quantity: numberOfBoostsSelected,
      },
    ];
    const boostPaymentArgs = {
      affiliateId: '',
      amount: amountToPay,
      context: '',
      currency,
      customerServiceContactDetails: {
        phoneNumber: phoneNumber,
        email: csEmail,
      },
      domain: domainString,
      paymentProvider: provider,
      platform,
      products,
      successCallback: paymentSuccessCallback,
      successUrl: `/${subDirectory}/mylistings.pl?boosts_purchased=${numberOfBoostsSelected}${
        advertId ? `&boosts_advert_id=${advertId}` : ''
      }#boosts-success`,
      token,
      userId,
    };
    return makePayment(boostPaymentArgs);
  };

  return (
    <>
      <form
        id="get-boosts"
        className="get-boosts__form"
        onSubmit={handleSubmit}
      >
        {/* need to make this accessible */}
        <label className="get-boosts__label" htmlFor="number-of-boosts">
          Number of Boosts
        </label>
        <div className="form-group__select">
          <select
            className="form-group__select-list"
            id="number-of-boosts"
            onChange={handleNumberOfBoostsChange}
          >
            {allowedArray.map((value) => (
              <option key={value} value={value}>
                Buy {value} {`Boost${value > 1 ? 's' : ''}`} ({currencySymbol}
                {(parseFloat(boostProductDetails.price) * value).toFixed(2)})
              </option>
            ))}
          </select>
          <span className="form-group__select-icon">
            <i aria-hidden className="far fa-chevron-down" />
          </span>
        </div>
        <div className="get-boosts__select"></div>
        <p className="get-boosts__text">
          You can only use 1 Boost per ad in any rolling 7-day period. You must
          also wait 7 days after creating or reactivating your ad to use Boosts.
        </p>
        <div className="get-boosts__button">
          <button className="button button--wide">Continue</button>
        </div>
        {isPaymentView ? (
          <PaymentForm
            staticFileServer={'/v2'}
            paymentSuccess={paymentSuccess}
            close={closeIsPaymentView}
            showDialog={isPaymentView}
            amount={amountToPay}
            currencySymbol={currencySymbol}
            localisations={localisations}
          />
        ) : null}
      </form>
    </>
  );
}
